import { PAGE_TYPES } from '@tn/foundation/utils/constants/tracking';
import { AnyStateConfig, assertEvent, enqueueActions, setup } from 'xstate';

import { FlowEntity, InitMachineParams } from '../../types';
import { devLog } from '../../utils/devLog';
import { resolveRegionalizedUrl } from '../rendezvous/resolveRegionalizedUrl';
import {
    getRendezvousAppLogFromSubmitError,
    trackActivationFailureEvent,
} from '../rendezvous/tracking';
import type { DeviceInfo, RendezvousFlowContext, TvCompleteOutput } from '../rendezvous/types';
import { DEFAULT_ACTIVATION_SCREEN, RENDEZVOUS_LOCAL_STORAGE_KEY } from '../rendezvous/types';

type TvCompleteContext = {
    url: string;
    activationDidFail: boolean;
} & Partial<DeviceInfo>;

/**
 * Input types:
 * TvCompleteBlueprintInput: from jsonc blueprint "input" field
 * TvCompleteInputActorInput: from inputActor
 * TvCompleteInput: merged input
 */
type TvCompleteBlueprintInput = Pick<TvCompleteContext, 'url'> & { includeRegionInUrl?: boolean };
type TvCompleteInputActorInput = Pick<TvCompleteContext, 'activationCode'> & {
    defaultRegionCode: string;
};
type TvCompleteInput = TvCompleteBlueprintInput & TvCompleteInputActorInput;

export const tvCompleteFlow: FlowEntity = {
    initActor: (rawParams) => {
        const { config, actors, router, region } = rawParams as InitMachineParams;
        return setup({
            types: {
                input: {} as TvCompleteInput,
                context: {} as TvCompleteContext,
            },
            guards: {},
            actions: {
                goToTvCompleteRoute: ({ context }) => {
                    router.push(context.url);
                },
                restoreActivationCodeIfMissing: enqueueActions(({ context, enqueue }) => {
                    try {
                        if (!context.activationCode) {
                            const storedDeviceInfo = globalThis.localStorage?.getItem(
                                RENDEZVOUS_LOCAL_STORAGE_KEY,
                            );

                            if (storedDeviceInfo) {
                                const parsedDeviceInfo = JSON.parse(storedDeviceInfo);
                                const activationCode =
                                    typeof parsedDeviceInfo?.activationCode === 'string'
                                        ? parsedDeviceInfo.activationCode.trim()
                                        : '';

                                if (activationCode) {
                                    enqueue.assign({
                                        activationCode: activationCode,
                                        activationScreen: undefined,
                                        device: undefined,
                                    });
                                }
                            }
                        }
                    } catch (e) {
                        devLog('[tvComplete] restoreActivationCodeIfMissing error', () => e);
                    }
                }),
                setActivationDidFail: enqueueActions(({ enqueue, event }) => {
                    devLog('[tvComplete] setActivationDidFail', () => event);
                    enqueue.assign({ activationDidFail: true });
                }),
                setDeviceInfo: enqueueActions(({ enqueue, event }) => {
                    assertEvent(event, 'xstate.done.actor.submitting');
                    devLog('[tvComplete] setActivationFields', () => event);
                    enqueue.assign({
                        activationCode: event.output.activationCode,
                        activationScreen:
                            event.output.activationScreen || DEFAULT_ACTIVATION_SCREEN,
                        device: event.output.device,
                    });
                }),
                trackActivationFailure: ({ context, event }) => {
                    devLog('[tvComplete] trackActivationFailure', () => event);
                    assertEvent(event, 'xstate.error.actor.submitting');

                    trackActivationFailureEvent({
                        activationCode: context.activationCode,
                        activationScreen: context.activationScreen,
                        device: context.device,
                        pageType: PAGE_TYPES.RENDEZVOUS_ACTIVATION_COMPLETE,
                        ...getRendezvousAppLogFromSubmitError(event.error),
                    });
                },
            },
            actors,
        }).createMachine({
            ...(config as AnyStateConfig),
            context: ({ input }) => ({
                ...input,
                url: resolveRegionalizedUrl(region, input),
                activationDidFail: false,
            }),
        });
    },
    inputActor: ({ context }: { context: RendezvousFlowContext }): TvCompleteInputActorInput => {
        devLog('[tvComplete] inputActor', () => context);

        return {
            activationCode: context.activationCode,
            defaultRegionCode: context.defaultRegionCode,
        };
    },
    outputActor: ({ context }: { context: TvCompleteContext }): TvCompleteOutput => {
        const output: TvCompleteOutput = {
            activationDidFail: context.activationDidFail,
        };

        if (!context.activationDidFail) {
            output.activationCode = context.activationCode;
            output.activationScreen = context.activationScreen ?? DEFAULT_ACTIVATION_SCREEN;
            output.device = context.device;
        }

        devLog('[tvComplete] outputActor', () => output);

        return output;
    },
};
